import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Minus, Plus, Check } from "lucide-react";

export const kicker = "text-[11px] uppercase tracking-[0.2em] text-[color:var(--gold-soft)]";
export const cardCls = "ascendia-card p-4 sm:p-6";

// Numeric stepper (rooms / bathrooms).
export const Stepper = ({ label, value, onChange, min = 0, max = 20, suffix = "", testid }) => {
  const v = Number(value) || 0;
  const set = (n) => onChange(Math.max(min, Math.min(max, n)));
  const btn = "h-10 w-10 flex items-center justify-center rounded-lg border border-[color:var(--border-subtle)] text-white/80 hover:bg-white/5 active:scale-95 disabled:opacity-40 transition-transform duration-150";
  return (
    <div>
      {label && <p className="text-sm text-secondary mb-1.5">{label}</p>}
      <div className="flex items-center gap-3">
        <button type="button" aria-label={`Decrease ${label}`} className={btn} onClick={() => set(v - 1)} disabled={v <= min} data-testid={`${testid}-dec`}><Minus className="h-4 w-4" /></button>
        <span className="min-w-[3ch] text-center font-display text-xl text-white tabular-nums" data-testid={testid}>{v}{suffix}</span>
        <button type="button" aria-label={`Increase ${label}`} className={btn} onClick={() => set(v + 1)} disabled={v >= max} data-testid={`${testid}-inc`}><Plus className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

// Selectable choice card (property type, need, etc.).
export const ChoiceCard = ({ active, title, desc, Icon, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    aria-pressed={active}
    className={`text-left rounded-xl border p-4 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ring)] ${active ? "border-gold bg-gold/10" : "border-[color:var(--border-subtle)] hover:bg-white/[0.03]"}`}>
    <div className="flex items-start gap-3">
      {Icon && <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${active ? "text-gold-soft" : "text-muted-c"}`} />}
      <div>
        <span className="block font-medium text-white">{title}</span>
        {desc && <span className="mt-1 block text-xs text-muted-c">{desc}</span>}
      </div>
      {active && <Check className="ml-auto h-4 w-4 text-gold" />}
    </div>
  </button>
);

// Segmented detail-level control with a sliding motion indicator (Essential / Deep / Ascendia Detail).
export const SegmentedLevels = ({ levels, value, onChange }) => {
  const reduce = useReducedMotion();
  return (
    <div className="ascendia-card p-1 grid grid-cols-3 gap-1" role="tablist" aria-label="Detail level" data-testid="detail-level-control">
      {levels.map((l) => {
        const active = value === l.key;
        return (
          <button key={l.key} role="tab" aria-selected={active} type="button"
            onClick={() => onChange(l.key)} data-testid={`detail-level-${l.key}`}
            className={`relative rounded-lg px-2 py-2.5 text-[11px] sm:text-xs uppercase tracking-[0.14em] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ring)] ${active ? "text-[color:var(--gold-soft)]" : "text-muted-c hover:text-white/80"}`}>
            {active && (
              <motion.span layoutId={reduce ? undefined : "detailLevelPill"}
                className="absolute inset-0 rounded-lg border border-[color:var(--border-strong)] bg-white/[0.04]"
                transition={{ type: "spring", stiffness: 380, damping: 32 }} />
            )}
            <span className="relative">{l.label}</span>
          </button>
        );
      })}
    </div>
  );
};

// Price range display.
export const PriceRange = ({ est }) => (
  <div data-testid="estimate-price-range">
    <p className={kicker}>Estimated price range (guide)</p>
    <p className="mt-1 font-display text-2xl sm:text-3xl font-semibold text-white tabular-nums">
      {est ? `${est.currency}${est.low.toLocaleString()} \u2013 ${est.currency}${est.high.toLocaleString()}` : "\u2014"}
    </p>
    <p className="text-[11px] text-muted-c mt-1">Not a guaranteed quote · confirmed after review</p>
  </div>
);

// Duration guide band.
export const DurationBand = ({ dur }) => (
  <div data-testid="estimate-duration-band">
    <p className={kicker}>Estimated duration (guide)</p>
    <div className="mt-2 flex items-center gap-3">
      <div className="relative h-1.5 flex-1 rounded-full bg-white/10 overflow-hidden">
        <div className="absolute inset-y-0 left-[15%] right-[15%] rounded-full bg-[color:var(--gold)]/70" />
      </div>
      <span className="text-sm text-white tabular-nums whitespace-nowrap">{dur.low}–{dur.high} hrs</span>
    </div>
    <p className="text-[11px] text-muted-c mt-1">{dur.band} · rough guide based on size</p>
  </div>
);

export const ComplexityBadge = ({ cx }) => {
  const map = { Low: "border-[color:var(--border-subtle)] text-white/70", Medium: "border-[color:var(--border-strong)] text-[color:var(--gold-soft)]", High: "border-[color:var(--border-strong)] text-[color:var(--gold-soft)]" };
  return (
    <div data-testid="estimate-complexity">
      <p className={kicker}>Complexity</p>
      <div className="mt-2 flex gap-2">
        {["Low", "Medium", "High"].map((l) => (
          <span key={l} className={`inline-flex items-center rounded-full border px-3 py-1 text-xs ${cx.level === l ? map[l] : "border-[color:var(--border-subtle)] text-muted-c/50"}`}>{l}</span>
        ))}
      </div>
    </div>
  );
};

export const Chip = ({ active, children, onClick, testid, dotted }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors duration-150 ${active ? "border-[color:var(--border-strong)] text-[color:var(--gold-soft)] bg-white/[0.03]" : "border-[color:var(--border-subtle)] text-secondary hover:bg-white/[0.03]"} ${dotted ? "underline decoration-dotted decoration-[color:var(--gold-soft)] underline-offset-4" : ""}`}>
    {children}
  </button>
);
